import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useHistory } from 'react-router-dom';
import moment from 'moment';
import { getSearchDateParamFromMoment } from 'utils/search/searchDateParam';
import { parseJsonFirstParam, extractSlugFromStopId } from 'utils/search/queryParamUtils';
import { createSearch } from '@/actions/search';
import LoadingScreen from 'components/LoadingScreen';

/**
 * GoogleTransitSearch component.
 * Handles search requests from Google Transport ticketing deep links.
 *
 * This component:
 * 1. Parses query parameters from the URL (service_date, from/to stop IDs, boarding_time, booking_token)
 * 2. Extracts origin and destination slugs from the stop IDs
 * 3. Determines the departure date from available parameters with fallbacks
 * 4. Dispatches a Redux action to create a search
 * 5. Redirects to the appropriate search URL with parameters
 *
 * Expected URL format: /search?service_date=...&from_ticketing_stop_time_id=...&to_ticketing_stop_time_id=...
 */

/**
 * Component that processes Google Transit deep links and redirects to the search page.
 * @returns {JSX.Element} Loading screen while processing the parameters
 */

const GoogleTransitSearch = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);

    try {
      // Parse parameters from query string
      const serviceDateParam = params.get('service_date');
      const fromStopIdParam = params.get('from_ticketing_stop_time_id');
      const toStopIdParam = params.get('to_ticketing_stop_time_id');
      const boardingTimeParam = params.get('boarding_time');
      const serviceDate = parseJsonFirstParam(serviceDateParam);
      const fromStopId = parseJsonFirstParam(fromStopIdParam);
      const toStopId = parseJsonFirstParam(toStopIdParam);
      const boardingTime = parseJsonFirstParam(boardingTimeParam);
      const bookingToken = params.get('booking_token');

      // Extract origin and destination from stop IDs
      const originSlug = extractSlugFromStopId(fromStopId);
      const destinationSlug = extractSlugFromStopId(toStopId);

      // Parse departure date from serviceDate or boardingTime
      // Date handling priority:
      // 1. Use serviceDate if available (format: YYYYMMDD)
      // 2. Use boardingTime if available (ISO format)
      // 3. Default to tomorrow if neither is available or valid

      let departureDate;
      if (serviceDate) {
        const parsedDate = moment(serviceDate, 'YYYYMMDD');
        departureDate = parsedDate.isValid()
          ? parsedDate.format('DD-MM-YYYY')
          : moment().add(1, 'days').format('DD-MM-YYYY');
      } else if (boardingTime) {
        const parsedDate = moment(boardingTime);
        departureDate = parsedDate.isValid()
          ? parsedDate.format('DD-MM-YYYY')
          : moment().add(1, 'days').format('DD-MM-YYYY');
      } else {
        departureDate = moment().add(1, 'days').format('DD-MM-YYYY');
      }

      // Default passenger count
      // Set default passenger count to 1 adult (A1)
      // Format: A represents adults, followed by the count
      const passengers = 'A1';

      if (originSlug && destinationSlug) {
        // Format date for URL
        const departureDateParam = getSearchDateParamFromMoment(
          moment(departureDate, 'DD-MM-YYYY'),
        );

        // Redirect to standard search URL format
        const searchUrl = `/search/${originSlug}/${destinationSlug}/${departureDateParam}/p/${passengers}/departures`;

        // Add optional booking token if present
        const searchUrlWithParams = bookingToken
          ? `${searchUrl}?booking_token=${bookingToken}`
          : searchUrl;

        // First dispatch the search action to populate the Redux store with search data
        // This ensures the search state is ready when the user lands on the search results page
        dispatch(
          createSearch(
            'departure',
            originSlug,
            destinationSlug,
            departureDate,
            passengers,
            false, // roundTrip
          ),
        );

        history.replace(searchUrlWithParams);
      } else {
        // Log which slug is missing to aid debugging
        if (!originSlug && !destinationSlug) {
          console.error('Both origin and destination slugs are invalid');
        } else if (!originSlug) {
          console.error('Origin slug is invalid:', fromStopId);
        } else {
          console.error('Destination slug is invalid:', toStopId);
        }
        history.replace('/');
      }
    } catch (error) {
      console.error('Error processing Google Transit search parameters:', error);
      history.replace('/');
    }
  }, [location, history, dispatch]);

  return <LoadingScreen resultMessages />;
};

export default GoogleTransitSearch;
